/**
 * Column definitions for the Reports > Redemptions blotter.
 *
 * Mirrors `admin/contacts/adminContactsColumns.tsx`'s conventions: `orDash` renders EMPTY_CELL
 * ("—") in the table, `orBlank` gives sort a "" for empty values — both come from
 * `utils/reportsRedemptions-helper.ts` rather than being redeclared here.
 *
 * `render`/`sortValue` are plain renderers called per row by the Blotter, and a
 * renderer may not call hooks — the Redemption Link cell's clipboard write is resolved by the
 * panel and passed down through `RedemptionsColumnDeps`, which is why `buildAllColumns` is a
 * function and not a module constant.
 *
 * Sent / Redeemed / Last Activity show the America/New_York calendar day (BUSINESS_TIMEZONE), so a
 * row reads the same day the API's date-range filter used to include it.
 */
import type { CSSProperties } from "react";
import RedemptionLinkCell from "@/components/admin/contacts/RedemptionLinkCell";
import type { BlotterColumn } from "@/components/Blotter";
import CampaignStatusChip from "@/components/campaigns/CampaignStatusChip";
import { BUSINESS_TIMEZONE } from "@/constants/dates";
import { COLUMN_LABELS, EMPTY_CELL, FIXED_COLUMN_LABEL } from "@/constants/reportsRedemptions";
import type { RedemptionColumnId, RedemptionRow } from "@/interfaces/reportsRedemptions";
import { applyPrefs, type ColumnPrefs } from "@/utils/columnPrefs-helper";
import { formatDate } from "@/utils/date-format";
import { formatUsd } from "@/utils/number-format";
import { orBlank, orDash, redemptionStatusDisplay } from "@/utils/reportsRedemptions-helper";

/** Hook-derived values the cells close over, resolved by the panel (Blotter's hooks-vs-renderers rule). */
export interface RedemptionsColumnDeps {
  /** Asks the panel to copy `url` to the clipboard; the panel performs the write and shows the toast. */
  onCopy: (url: string) => void;
}

/**
 * Base cell style. `cellStyle` **replaces** the Blotter's own `td` rather than merging with it,
 * so a column needing one extra property has to restate the rest (see adminContactsColumns.tsx).
 */
const TD: CSSProperties = {
  padding: "14px 16px",
  fontSize: 14,
  color: "var(--color-rodeo-black)",
  borderBottom: "1px solid var(--color-smoke)",
  whiteSpace: "nowrap",
};

/** The redemption URL is the one cell long enough to need clamping for its ellipsis to trigger. */
const LINK_TD: CSSProperties = { ...TD, maxWidth: 280 };

const MUTED: CSSProperties = { color: "var(--color-graphite)" };
const PRIMARY: CSSProperties = { fontWeight: 600 };

/**
 * Every configurable column keyed by id, ready for `applyPrefs`.
 *
 * A function rather than a module constant because the Redemption Link cell needs the panel's
 * `onCopy` (see the file doc above).
 */
export function buildAllColumns(
  deps: RedemptionsColumnDeps,
): Record<RedemptionColumnId, BlotterColumn<RedemptionRow>> {
  return {
    email: {
      key: "email",
      label: COLUMN_LABELS.email,
      sortable: true,
      sortValue: (r) => orBlank(r.recipientEmail),
      render: (r) => <span style={MUTED}>{orDash(r.recipientEmail)}</span>,
    },
    company: {
      key: "company",
      label: COLUMN_LABELS.company,
      sortable: true,
      sortValue: (r) => orBlank(r.recipientCompany),
      render: (r) => orDash(r.recipientCompany),
    },
    status: {
      key: "status",
      label: COLUMN_LABELS.status,
      sortable: true,
      sortValue: (r) => r.status,
      render: (r) => <CampaignStatusChip status={redemptionStatusDisplay(r.status)} size="sm" />,
    },
    campaign: {
      key: "campaign",
      label: COLUMN_LABELS.campaign,
      sortable: true,
      sortValue: (r) => r.campaignName,
      render: (r) => orDash(r.campaignName),
    },
    sender: {
      key: "sender",
      label: COLUMN_LABELS.sender,
      sortable: true,
      sortValue: (r) => orBlank(r.senderName),
      render: (r) => orDash(r.senderName),
    },
    product: {
      key: "product",
      label: COLUMN_LABELS.product,
      sortable: true,
      sortValue: (r) => orBlank(r.productTitle),
      render: (r) => orDash(r.productTitle),
    },
    giftImg: {
      // No sort expression on the API side — the whitelist excludes it.
      key: "giftImg",
      label: COLUMN_LABELS.giftImg,
      render: (r) =>
        r.giftImage ? (
          <img
            src={r.giftImage}
            alt={r.productTitle ?? "Gift"}
            width={32}
            height={32}
            loading="lazy"
            style={{ objectFit: "cover", borderRadius: 4 }}
          />
        ) : (
          EMPTY_CELL
        ),
    },
    giftValue: {
      key: "giftValue",
      label: COLUMN_LABELS.giftValue,
      sortable: true,
      sortValue: (r) => r.giftValue ?? 0,
      render: (r) => formatUsd(r.giftValue),
    },
    sentAt: {
      key: "sentAt",
      label: COLUMN_LABELS.sentAt,
      sortable: true,
      sortValue: (r) => orBlank(r.sentAt),
      render: (r) => <span style={MUTED}>{formatDate(r.sentAt, BUSINESS_TIMEZONE)}</span>,
    },
    redeemedAt: {
      key: "redeemedAt",
      label: COLUMN_LABELS.redeemedAt,
      sortable: true,
      sortValue: (r) => orBlank(r.redeemedAt),
      render: (r) => <span style={MUTED}>{formatDate(r.redeemedAt, BUSINESS_TIMEZONE)}</span>,
    },
    lastActivity: {
      key: "lastActivity",
      label: COLUMN_LABELS.lastActivity,
      sortable: true,
      sortValue: (r) => orBlank(r.lastActivity),
      render: (r) => <span style={MUTED}>{formatDate(r.lastActivity, BUSINESS_TIMEZONE)}</span>,
    },
    reminderCount: {
      key: "reminderCount",
      label: COLUMN_LABELS.reminderCount,
      sortable: true,
      sortValue: (r) => r.reminderCount,
      render: (r) => orDash(String(r.reminderCount)),
    },
    shipName: {
      key: "shipName",
      label: COLUMN_LABELS.shipName,
      sortable: true,
      sortValue: (r) => orBlank(r.shippingName),
      render: (r) => orDash(r.shippingName),
    },
    shipAddr: {
      key: "shipAddr",
      label: COLUMN_LABELS.shipAddr,
      sortable: true,
      sortValue: (r) => orBlank(r.shippingAddress),
      render: (r) => orDash(r.shippingAddress),
    },
    shipCity: {
      key: "shipCity",
      label: COLUMN_LABELS.shipCity,
      sortable: true,
      sortValue: (r) => orBlank(r.shippingCity),
      render: (r) => orDash(r.shippingCity),
    },
    shipState: {
      key: "shipState",
      label: COLUMN_LABELS.shipState,
      sortable: true,
      sortValue: (r) => orBlank(r.shippingState),
      render: (r) => orDash(r.shippingState),
    },
    shipPostal: {
      key: "shipPostal",
      label: COLUMN_LABELS.shipPostal,
      sortable: true,
      sortValue: (r) => orBlank(r.shippingPostal),
      render: (r) => orDash(r.shippingPostal),
    },
    shipCountry: {
      key: "shipCountry",
      label: COLUMN_LABELS.shipCountry,
      sortable: true,
      sortValue: (r) => orBlank(r.shippingCountry),
      render: (r) => orDash(r.shippingCountry),
    },
    url: {
      // No sort expression on the API side — the whitelist excludes it.
      key: "url",
      label: COLUMN_LABELS.url,
      cellStyle: LINK_TD,
      render: (r) =>
        r.redemptionUrl ? (
          <RedemptionLinkCell url={r.redemptionUrl} onCopy={deps.onCopy} />
        ) : (
          EMPTY_CELL
        ),
    },
  };
}

/**
 * The columns to hand the Blotter: Recipient Name first and always, then the configurable
 * columns the user left visible, in the order they arranged them.
 */
export function buildColumns(
  prefs: ColumnPrefs<RedemptionColumnId>,
  deps: RedemptionsColumnDeps,
): BlotterColumn<RedemptionRow>[] {
  const fixed: BlotterColumn<RedemptionRow> = {
    key: "name",
    label: FIXED_COLUMN_LABEL,
    sortable: true,
    sortValue: (r) => r.recipientName,
    render: (r) => <span style={PRIMARY}>{r.recipientName}</span>,
  };
  return [fixed, ...applyPrefs(buildAllColumns(deps), prefs)];
}
